/**
 * Playlist engine for the table read.
 *
 * Separated from the view because this is where the two rules that matter live:
 *
 *  1. Narration and speaker-name policy are applied HERE, by filtering segments at
 *     playback time. v0.3 §14: "first per playback: resolve at playlist/assembly time;
 *     do not rerender dialogue." Changing a setting must never cost a render.
 *
 *  2. The current LINE is derived from the current segment's position plus member
 *     offsets — never from the segment itself. That is what makes the player indifferent
 *     to whether a take covers one line or a span of them (§6.2, still an open question
 *     in POC 0).
 */

export function buildPlaylist(recipe, settings) {
  const segs = (recipe?.segments ?? []).filter((s) => {
    if (s.kind === 'narration') return settings.narration !== 'none';
    if (s.kind === 'speaker_name') return keepName(s, settings);
    return true;
  });

  let at = 0;
  const items = segs.map((s, i) => {
    const start = at;
    at += (s.duration_ms || 0) + (i < segs.length - 1 ? (s.gap_after_ms || 0) : 0);
    return { ...s, index: i, global_start_ms: start, global_end_ms: start + (s.duration_ms || 0) };
  });
  return { items, total_ms: at };
}

function keepName(seg, settings) {
  const mode = settings.speaker_names;
  if (mode === 'never') return false;
  if (mode === 'always') return true;
  // first_per_scene / first_per_playback differ only in scope; within one scene read the
  // player resolves both the same way — keep the first clip per character.
  return seg.__firstForCharacter === true;
}

/** Mark the first speaker-name clip per character so keepName can filter on it. */
export function annotateFirstNames(recipe) {
  const seen = new Set();
  return {
    ...recipe,
    segments: (recipe?.segments ?? []).map((s) => {
      if (s.kind !== 'speaker_name') return s;
      const who = s.members?.[0]?.character ?? '?';
      const first = !seen.has(who);
      seen.add(who);
      return { ...s, __firstForCharacter: first };
    }),
  };
}

/**
 * Which member of THIS segment is sounding at `msIntoSegment`.
 *
 * In the gap BETWEEN two members of a span, hold the one that already started rather than
 * jumping to the next: the highlight must not move to a line before it is spoken. Returning
 * the last member here made the transcript run ahead of the audio during every inter-line
 * pause, which reads as a broken player.
 */
export function memberAt(segment, msIntoSegment) {
  const ms = Math.max(0, msIntoSegment);
  const list = segment?.members ?? [];
  let held = list[0] ?? null;
  for (const m of list) {
    if (ms >= (m.start_ms ?? 0) && ms < (m.end_ms ?? 0)) return m;
    if (ms >= (m.start_ms ?? 0)) held = m;   // started already; keep holding it
  }
  return held;
}

/** Flatten to a transcript: one row per member, with the segment it belongs to. */
export function transcript(items) {
  const rows = [];
  items.forEach((seg) => {
    // Name clips are playback furniture, not script: the script view shows the line itself.
    if (seg.kind === 'speaker_name') return;
    (seg.members ?? []).forEach((m, k) => {
      rows.push({
        key: `${seg.id}:${k}`,
        segIndex: seg.index,
        kind: seg.kind,
        character: m.character,
        text: m.text,
        seek_ms: seg.global_start_ms + (m.start_ms ?? 0),
        method: m.method,
        spanned: (seg.members?.length ?? 1) > 1,
      });
    });
  });
  return rows;
}

export const fmt = (ms) => {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
